import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Bulletin } from '@shared/types/Bulletin';
import type { DeclarationDts, StatutDts } from '@shared/types/Dts';
import type { Salarie } from '@shared/types/Salarie';
import type { TypeDeclaration } from '@infra/export/DeclarationsExporter';
import { DeclarationsExporter } from '@infra/export/DeclarationsExporter';
import { DtsOfficielExcelExporter } from '@infra/export/DtsOfficielExcelExporter';
import { DtsOfficiel } from '@domain/fiscalite/DtsOfficiel';
import { PARAMETRES_DTS_PAR_DEFAUT, type ParametresDts } from '@domain/fiscalite/DtsParametres';
import { loadParametresDts, saveParametresDts } from '@application/fiscalite/DtsParametresStore';
import { mountDtsParametresPanel } from '@presentation/views/declarations/DtsParametresPanel';
import { notifierToast } from '@presentation/views/comptabilite/panelKit';
import { DASService } from '@domain/fiscalite/DASService';
import {
  DtsService,
  libelleTrimestre,
  optionsTrimestres,
  periodesDuTrimestre,
  trimestreDePeriode,
} from '@domain/fiscalite/DtsService';
import {
  creerDts,
  deposerDts,
  listDts,
  mettreAJourDts,
  regenererDts,
  supprimerDts,
} from '@application/fiscalite/DtsDirectoryService';
import {
  annulerReversementIRPP,
  enregistrerReversementIRPP,
  getReversementIRPP,
} from '@application/fiscalite/ReversementIRPPDirectoryService';
import { ReversementIRPPService } from '@domain/fiscalite/ReversementIRPPService';
import { Ad01CnssService } from '@domain/fiscalite/Ad01CnssService';
import { ventilationCnssDuBulletin } from '@domain/paie/VentilationCnss';
import { salariesSansIdentifiant } from '@domain/fiscalite/IdentifiantsManquants';
import { DeclarationCnamgsMensuelleService } from '@domain/fiscalite/DeclarationCnamgsMensuelleService';
import type { EtatReversementIRPP } from '@shared/types/ReversementIRPP';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { buildDAS1HTML } from '@presentation/print/das1Template';
import { buildDtsHTML } from '@presentation/print/dtsTemplate';
import { previewDocument } from '@presentation/print/PdfService';
import { downloadCsv, downloadExcel } from '@presentation/shell/download';
import { ToastService } from '@presentation/components/Toast';
import { numeroCnss, numeroCnamgs, nifGabon } from '@shared/validation/identifiantsGabon';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { FormHelper } from '@presentation/components/FormHelper';
import { openFormModal } from '@presentation/shell/openFormModal';
import { renderRowActions } from '@presentation/shell/rowActions';
import { onExclusive } from '@presentation/shell/viewDom';
import { DtsCreateSchema, DtsUpdateSchema } from '@shared/validation/DtsSchema';
import { COMPANY_LEGAL_WARNING, isCompanyLegalComplete } from '@domain/legal/CompanyLegalInfo';
import { fmtFCFA, fmtPeriode, esc } from '@presentation/shell/format';
import { periodeCouranteFromTenant } from '@presentation/shell/periodeCourante';

const STATUT_DTS: Record<StatutDts, string> = {
  brouillon: 'Brouillon',
  deposee: 'Déposée',
  annulee: 'Annulée',
};

const LIB_STATUT_IRPP: Record<EtatReversementIRPP['statut'], string> = {
  a_reverser: 'À reverser',
  reverse: 'Reversé',
  en_retard: 'En retard',
};

function tenantIdOf(ctx: { tenantContext: { tryGetTenant: () => { id: string } | null } }): string {
  return ctx.tenantContext.tryGetTenant()?.id ?? 'default';
}

function attrEscape(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

export const declarationsController: ViewController = async (section, ctx, shell) => {
  const tenantId = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  const [bulletins, salaries, declarations] = await Promise.all([
    ctx.bulletins.list(),
    ctx.salaries.list(),
    listDts(tenantId),
  ]);
  const parId = new Map(salaries.map((s) => [s.id, s]));
  const tenant = ctx.tenantContext.tryGetTenant();
  const periodes = Array.from(new Set(bulletins.map((b) => b.periode))).sort((a, b) =>
    b.localeCompare(a),
  );
  let current = periodeCouranteFromTenant(tenant, { periodes });
  let recordIrpp = await getReversementIRPP(tenantId, current);

  let parametresDts: ParametresDts = PARAMETRES_DTS_PAR_DEFAUT;
  const hoteParametresDts = section.querySelector<HTMLElement>('#dts-parametres-host');
  if (hoteParametresDts) {
    await mountDtsParametresPanel(hoteParametresDts, {
      canMutate,
      load: async () => {
        parametresDts = await loadParametresDts(tenantId);
        return parametresDts;
      },
      save: (saisie) => saveParametresDts(tenantId, saisie),
      audit: (description, payload) =>
        recordAuditEvent(ctx, {
          action: 'update',
          entity: 'parametres',
          description,
          ...(payload ? { payload } : {}),
        }),
      notify: notifierToast,
      onChange: (p) => {
        parametresDts = p;
      },
    });
  } else {
    parametresDts = await loadParametresDts(tenantId);
  }

  const display = section.querySelector('#periode-display-3');
  const btnNew = section.querySelector<HTMLElement>('#btn-new-dts');
  if (btnNew) btnNew.style.display = canMutate ? '' : 'none';

  const etatIrppCourant = (): EtatReversementIRPP =>
    ReversementIRPPService.genererEtat(bulletins, current, tenant, recordIrpp);

  const render = (): void => {
    const list = bulletins.filter(
      (b) => b.periode === current && DeclarationsExporter.bulletinEligible(b),
    );
    if (display) display.textContent = fmtPeriode(current);
    renderKpis(section, list);
    renderReversementIRPP(section, etatIrppCourant(), canMutate);
    renderCnss(section, list, parId);
    renderCnamgs(section, list, parId);
    renderDgi(section, list, parId);
    renderDtsTable(section, declarations, canMutate);
  };

  render();

  const exporter = (type: TypeDeclaration): void => {
    if (type === 'cnss' || type === 'cnamgs') {
      void exporterDtsOrganisme(type);
      return;
    }
    const libelle = DeclarationsExporter.LIBELLES[type];
    const eligibles = bulletins.filter(
      (b) => b.periode === current && DeclarationsExporter.bulletinEligible(b),
    );
    if (eligibles.length === 0) {
      ToastService.error(
        `Aucun bulletin validé ou payé pour ${fmtPeriode(current)} — export ${libelle} impossible`,
      );
      return;
    }
    const parseId = nifGabon;
    const manquants = eligibles
      .map((b) => parId.get(b.salarie_id))
      .filter((s): s is Salarie => Boolean(s))
      .filter((s) => !parseId.safeParse(DeclarationsExporter.identifiant(type, s)).success);
    if (manquants.length > 0) {
      ToastService.warning(
        `${manquants.length} salarié(s) sans NIF renseigné : ${manquants
          .slice(0, 3)
          .map((s) => s.matricule)
          .join(', ')}`,
      );
    }
    const fichier = DeclarationsExporter.nomFichier(type, current);
    const entreprise = ctx.tenantContext.tryGetTenant();
    downloadCsv(
      DeclarationsExporter.genererCSV(type, eligibles, salaries, current, entreprise),
      fichier,
    );
    ToastService.success(`Déclaration ${libelle} exportée (${fichier})`);
  };

  const bandeauAvertissements = section.querySelector<HTMLElement>('#declarations-avertissements');
  const afficherAvertissements = (lignes: readonly string[]): void => {
    if (!bandeauAvertissements) return;
    bandeauAvertissements.innerHTML = lignes
      .map((l) => `<p style="margin:0 0 6px;">${l}</p>`)
      .join('');
    bandeauAvertissements.style.display = lignes.length > 0 ? '' : 'none';
  };

  /**
   * Avertit, sans jamais bloquer l'export, des salariés sans numéro CNSS / CNAMGS (liste complète dans le
   * bandeau de l'écran, qui reste affiché) et d'un numéro employeur manquant.
   */
  const avertirIdentifiants = (type: 'cnss' | 'cnamgs', trimestre: string): void => {
    const periodes = new Set(periodesDuTrimestre(trimestre));
    const eligibles = bulletins.filter(
      (b) => periodes.has(b.periode) && DeclarationsExporter.bulletinEligible(b),
    );
    const parseId = type === 'cnss' ? numeroCnss : numeroCnamgs;
    const libId = type === 'cnss' ? 'CNSS' : 'CNAMGS';
    const manquants = salariesSansIdentifiant(
      eligibles,
      salaries,
      (s) => DeclarationsExporter.identifiant(type, s),
      (identifiant) => parseId.safeParse(identifiant).success,
    );
    const lignes: string[] = [];
    if (manquants.length > 0) {
      ToastService.warning(
        `${manquants.length} salarié(s) sans N° ${libId} : ${manquants
          .slice(0, 3)
          .map((s) => s.matricule)
          .join(
            ', ',
          )}${manquants.length > 3 ? '…' : ''} — liste complète affichée en haut de l'écran`,
      );
      lignes.push(
        `<strong>Export ${esc(libId)} : ${manquants.length} salarié(s) sans N° ${esc(libId)}.</strong> ` +
          `L'export a été fait, mais ces salariés apparaissent sans numéro : renseignez-le sur leur fiche puis exportez à nouveau.<br>` +
          manquants.map((s) => `${esc(s.matricule)} — ${esc(s.nom)}`).join(' · '),
      );
    }
    const entreprise = ctx.tenantContext.tryGetTenant();
    const matricule =
      type === 'cnss'
        ? entreprise?.parametres?.numero_cnss_employeur
        : entreprise?.parametres?.numero_cnamgs_employeur;
    if (!matricule?.trim()) {
      ToastService.warning(
        `Renseignez le N° ${libId} employeur dans SYSTÈME → Paramètres → Identité légale.`,
      );
      lignes.push(
        `Le N° ${esc(libId)} employeur n'est pas renseigné : SYSTÈME → Paramètres → Identité légale.`,
      );
    }
    afficherAvertissements(lignes);
  };

  const exporterDtsOrganisme = async (
    type: 'cnss' | 'cnamgs',
    trimestreForce?: string,
  ): Promise<void> => {
    let trimestre: string;
    try {
      trimestre = trimestreForce ?? trimestreDePeriode(current);
    } catch {
      ToastService.error('Période invalide — export DTS impossible');
      return;
    }
    try {
      const entreprise = ctx.tenantContext.tryGetTenant();
      const dtsEnregistree = declarations.find((d) => d.trimestre === trimestre);
      const agrege = DtsOfficiel.agreger(
        bulletins,
        salaries,
        trimestre,
        entreprise,
        dtsEnregistree?.reference_depot,
        parametresDts,
      );
      avertirIdentifiants(type, trimestre);
      if (type === 'cnss') {
        downloadExcel(
          await DtsOfficielExcelExporter.classeurCnss(agrege),
          DtsOfficielExcelExporter.nomFichierCnss(agrege),
        );
      } else {
        downloadExcel(
          await DtsOfficielExcelExporter.classeurCnamgs(agrege),
          DtsOfficielExcelExporter.nomFichierCnamgs(agrege),
        );
      }
      ToastService.success(
        `DTS ${libelleTrimestre(trimestre)} exportée (fichier ${type.toUpperCase()} officiel)`,
      );
    } catch (error) {
      ToastService.error(error instanceof Error ? error.message : 'Export DTS impossible');
    }
  };

  const refresh = async (): Promise<void> => {
    await declarationsController(section, ctx, shell);
  };

  const openCreate = (): void => {
    if (!canMutate) return;
    let defaut: string;
    try {
      defaut = trimestreDePeriode(current);
    } catch {
      defaut = optionsTrimestres(periodes)[0]?.value ?? '';
    }
    openFormModal(section, { title: 'Nouvelle DTS', size: 'md' }, (host, close) => {
      host.innerHTML = renderDtsCreateForm(periodes, defaut);
      host.querySelector('[data-cancel]')?.addEventListener('click', close);
      const form = host.querySelector<HTMLFormElement>('#dts-form')!;
      FormHelper.bindSubmit(form, DtsCreateSchema, async (data) => {
        const existant = declarations.find(
          (d) => d.trimestre === data.trimestre && d.statut === 'brouillon',
        );
        if (existant) {
          const ok = await confirmAction({
            title: 'Brouillon existant',
            message: `Un brouillon DTS existe déjà pour ${libelleTrimestre(data.trimestre)}. Le régénérer depuis les bulletins ?`,
            confirmLabel: 'Régénérer',
            host: section,
          });
          if (!ok) return;
        }
        const created = await creerDts(tenantId, bulletins, salaries, data.trimestre, {
          notes: data.notes,
          remplacerBrouillon: Boolean(existant),
        });
        await recordAuditEvent(ctx, {
          action: existant ? 'update' : 'create',
          entity: 'dts',
          entity_id: created.id,
          description: existant
            ? `Régénération DTS ${created.trimestre}`
            : `Création DTS ${created.trimestre}`,
        });
        ToastService.success(`DTS ${libelleTrimestre(created.trimestre)} enregistrée`);
        close();
        await refresh();
      });
    });
  };

  const openEdit = (dts: DeclarationDts): void => {
    if (!canMutate || dts.statut === 'annulee') return;
    openFormModal(
      section,
      { title: `Modifier DTS ${libelleTrimestre(dts.trimestre)}`, size: 'md' },
      (host, close) => {
        host.innerHTML = renderDtsEditForm(dts);
        host.querySelector('[data-cancel]')?.addEventListener('click', close);
        const form = host.querySelector<HTMLFormElement>('#dts-edit-form')!;
        FormHelper.bindSubmit(form, DtsUpdateSchema, async (data) => {
          await mettreAJourDts(tenantId, dts.id, {
            notes: data.notes,
            reference_depot: data.reference_depot,
          });
          await recordAuditEvent(ctx, {
            action: 'update',
            entity: 'dts',
            entity_id: dts.id,
            description: `Mise à jour DTS ${dts.trimestre}`,
          });
          ToastService.success('DTS mise à jour');
          close();
          await refresh();
        });
      },
    );
  };

  const voir = (dts: DeclarationDts): void => {
    const tenant = ctx.tenantContext.tryGetTenant();
    if (!isCompanyLegalComplete(tenant)) {
      ToastService.warning(COMPANY_LEGAL_WARNING);
      return;
    }
    const doc = buildDtsHTML(dts, tenant);
    previewDocument(doc.html, doc.filename, doc.title);
  };

  const exporterDts = (dts: DeclarationDts): void => {
    downloadCsv(
      DtsService.exporterCsv(dts, ctx.tenantContext.tryGetTenant()),
      DtsService.nomFichier(dts.trimestre),
    );
    ToastService.success(`DTS ${dts.trimestre} exportée`);
  };

  const exporterDtsMensuel = async (dts: DeclarationDts): Promise<void> => {
    const [bulletins, salaries] = await Promise.all([ctx.bulletins.list(), ctx.salaries.list()]);
    const periodes = new Set(periodesDuTrimestre(dts.trimestre));
    const duTrimestre = bulletins.filter((b) => periodes.has(b.periode));
    downloadCsv(
      DtsService.exporterMensuel(duTrimestre, salaries, tenant),
      `declaration_dts_mensuel_${dts.trimestre}.csv`,
    );
    ToastService.success(`DTS mensuelle ${dts.trimestre} exportée (format dépôt cnss.ga)`);
  };

  const deposer = async (dts: DeclarationDts): Promise<void> => {
    if (!canMutate || dts.statut !== 'brouillon') return;
    const ok = await confirmAction({
      title: 'Déposer la DTS',
      message: `Déposer la DTS ${libelleTrimestre(dts.trimestre)} ? Elle ne pourra plus être régénérée.`,
      confirmLabel: 'Déposer',
      host: section,
    });
    if (!ok) return;
    await deposerDts(tenantId, dts.id);
    await recordAuditEvent(ctx, {
      action: 'update',
      entity: 'dts',
      entity_id: dts.id,
      description: `Dépôt DTS ${dts.trimestre}`,
    });
    ToastService.success(`DTS ${dts.trimestre} déposée`);
    await refresh();
  };

  const regenerer = async (dts: DeclarationDts): Promise<void> => {
    if (!canMutate || dts.statut !== 'brouillon') return;
    const ok = await confirmAction({
      title: 'Régénérer la DTS',
      message: `Recalculer la DTS ${libelleTrimestre(dts.trimestre)} depuis les bulletins du trimestre ?`,
      confirmLabel: 'Régénérer',
      host: section,
    });
    if (!ok) return;
    const [freshBulletins, freshSalaries] = await Promise.all([
      ctx.bulletins.list(),
      ctx.salaries.list(),
    ]);
    await regenererDts(tenantId, dts.id, freshBulletins, freshSalaries);
    await recordAuditEvent(ctx, {
      action: 'update',
      entity: 'dts',
      entity_id: dts.id,
      description: `Régénération DTS ${dts.trimestre}`,
    });
    ToastService.success('DTS régénérée');
    await refresh();
  };

  const supprimer = async (dts: DeclarationDts): Promise<void> => {
    if (!canMutate || dts.statut === 'annulee') return;
    const brouillon = dts.statut === 'brouillon';
    const ok = await confirmAction({
      title: brouillon ? 'Supprimer le brouillon' : 'Annuler la DTS',
      message: brouillon
        ? `Supprimer le brouillon DTS ${libelleTrimestre(dts.trimestre)} ?`
        : `Annuler la DTS déposée ${libelleTrimestre(dts.trimestre)} ? Elle restera consultable comme annulée.`,
      confirmLabel: brouillon ? 'Supprimer' : 'Annuler la déclaration',
      danger: true,
      host: section,
    });
    if (!ok) return;
    const result = await supprimerDts(tenantId, dts.id);
    await recordAuditEvent(ctx, {
      action: 'delete',
      entity: 'dts',
      entity_id: dts.id,
      description:
        result.statut === 'supprimee'
          ? `Suppression brouillon DTS ${dts.trimestre}`
          : `Annulation DTS ${dts.trimestre}`,
    });
    ToastService.success(result.statut === 'supprimee' ? 'Brouillon DTS supprimé' : 'DTS annulée');
    await refresh();
  };

  const w = window as unknown as Record<string, unknown>;
  w['changePeriode'] = (delta: number) => {
    const [y, m] = current.split('-').map(Number);
    const d = new Date(y ?? 2026, (m ?? 1) - 1 + delta, 1);
    current = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    void getReversementIRPP(tenantId, current).then((r) => {
      recordIrpp = r;
      render();
    });
  };
  w['exportCNSS'] = () => exporter('cnss');
  w['exportCNAMGS'] = () => exporter('cnamgs');
  w['exportDGI'] = () => exporter('dgi');
  w['exportAD01'] = () => {
    let trimestre: string;
    try {
      trimestre = trimestreDePeriode(current);
    } catch {
      ToastService.error('Période invalide — export AD-01 impossible');
      return;
    }
    try {
      const agrege = Ad01CnssService.agreger(bulletins, salaries, trimestre);
      avertirIdentifiants('cnss', trimestre);
      downloadCsv(
        Ad01CnssService.exporterCsv(agrege, ctx.tenantContext.tryGetTenant()),
        Ad01CnssService.nomFichier(trimestre),
      );
      ToastService.success(`AD-01 ${libelleTrimestre(trimestre)} exporté`);
    } catch (error) {
      ToastService.error(error instanceof Error ? error.message : 'Export AD-01 impossible');
    }
  };
  w['exportCnamgsMensuel'] = () => {
    try {
      const etat = DeclarationCnamgsMensuelleService.genererEtat(
        bulletins,
        salaries,
        current,
        tenant,
      );
      if (etat.effectif === 0) {
        ToastService.error(
          `Aucun bulletin validé ou payé pour ${fmtPeriode(current)} — export CNAMGS mensuel impossible`,
        );
        return;
      }
      avertirIdentifiants('cnamgs', trimestreDePeriode(current));
      downloadCsv(
        DeclarationCnamgsMensuelleService.exporterCsv(etat, tenant),
        DeclarationCnamgsMensuelleService.nomFichier(current),
      );
      ToastService.success(`CNAMGS mensuelle ${current} exportée`);
    } catch (error) {
      ToastService.error(
        error instanceof Error ? error.message : 'Export CNAMGS mensuel impossible',
      );
    }
  };
  w['exportReversementIRPP'] = () => {
    const etat = etatIrppCourant();
    const lignes = ReversementIRPPService.lignesNominatives(bulletins, salaries, current);
    downloadCsv(
      ReversementIRPPService.exporterCsv(etat, tenant, lignes),
      ReversementIRPPService.nomFichier(current),
    );
    ToastService.success(`État reversement IRPP ${current} exporté`);
  };
  w['marquerReversementIRPP'] = () => {
    if (!canMutate) return;
    openFormModal(
      section,
      { title: `Reversement IRPP — ${fmtPeriode(current)}`, size: 'md' },
      (host, close) => {
        const today = new Date();
        const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        host.innerHTML = `<form id="irpp-rev-form">
          ${FormHelper.field('date_reversement', 'Date de reversement', 'date', {
            required: true,
            value: recordIrpp?.date_reversement ?? iso,
          })}
          ${FormHelper.field('reference_paiement', 'Référence de paiement', 'text', {
            value: recordIrpp?.reference_paiement ?? '',
            placeholder: 'N° quittance DGI',
          })}
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
            <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
            <button type="submit" class="btn btn-primary">Enregistrer</button>
          </div>
        </form>`;
        host.querySelector('[data-cancel]')?.addEventListener('click', close);
        const form = host.querySelector<HTMLFormElement>('#irpp-rev-form')!;
        form.addEventListener('submit', (ev) => {
          ev.preventDefault();
          void (async () => {
            const fd = new FormData(form);
            const date = String(fd.get('date_reversement') || '').trim();
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
              ToastService.error('Date de reversement invalide');
              return;
            }
            const ref = String(fd.get('reference_paiement') || '').trim();
            recordIrpp = await enregistrerReversementIRPP(tenantId, current, {
              date_reversement: date,
              reference_paiement: ref || undefined,
            });
            await recordAuditEvent(ctx, {
              action: 'update',
              entity: 'reversement_irpp',
              entity_id: recordIrpp.id,
              description: `Reversement IRPP ${current} enregistré`,
            });
            ToastService.success('Reversement IRPP enregistré');
            close();
            render();
          })();
        });
      },
    );
  };
  w['annulerMarquageIRPP'] = () => {
    if (!canMutate || !recordIrpp) return;
    void (async () => {
      const ok = await confirmAction({
        title: 'Annuler le marquage',
        message: `Retirer le statut « Reversé » pour ${fmtPeriode(current)} ?`,
        confirmLabel: 'Annuler le marquage',
        danger: true,
        host: section,
      });
      if (!ok) return;
      await annulerReversementIRPP(tenantId, current);
      recordIrpp = undefined;
      ToastService.success('Marquage reversement IRPP annulé');
      render();
    })();
  };
  w['genererDAS'] = () => {
    const tenant = ctx.tenantContext.tryGetTenant();
    if (!isCompanyLegalComplete(tenant)) {
      ToastService.warning(COMPANY_LEGAL_WARNING);
      return;
    }
    const annee = current.slice(0, 4);
    const eligibles = bulletins.filter(
      (b) => b.periode.startsWith(annee) && DeclarationsExporter.bulletinEligible(b),
    );
    if (eligibles.length === 0) {
      ToastService.error(
        `Aucun bulletin validé ou payé sur l'exercice ${annee} — DAS 1 impossible`,
      );
      return;
    }
    const das = DASService.genererDAS1(eligibles, salaries, annee);
    if (das.lignes.length === 0) {
      ToastService.error(
        `Exercice ${annee} : aucun salarié rattaché aux bulletins — DAS 1 impossible`,
      );
      return;
    }
    const doc = buildDAS1HTML(das, tenant);
    previewDocument(doc.html, doc.filename, doc.title);
  };
  w['openDtsModal'] = openCreate;

  onExclusive(section, 'click', (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!btn || btn.closest('#dts-table') === null) return;
    const id = btn.dataset.id;
    const action = btn.dataset.action;
    if (!id || !action) return;
    void (async () => {
      try {
        const latest = await listDts(tenantId);
        const dts = latest.find((d) => d.id === id);
        if (!dts) {
          ToastService.error('Déclaration introuvable');
          return;
        }
        if (action === 'view') {
          voir(dts);
          return;
        }
        if (action === 'csv') {
          exporterDts(dts);
          return;
        }
        if (action === 'excel-cnss') {
          await exporterDtsOrganisme('cnss', dts.trimestre);
          return;
        }
        if (action === 'excel-cnamgs') {
          await exporterDtsOrganisme('cnamgs', dts.trimestre);
          return;
        }
        if (action === 'csv-mensuel') {
          await exporterDtsMensuel(dts);
          return;
        }
        if (!canMutateRecords(shell.permission)) return;
        if (action === 'edit') openEdit(dts);
        else if (action === 'deposit') await deposer(dts);
        else if (action === 'regen') await regenerer(dts);
        else if (action === 'delete') await supprimer(dts);
      } catch (error) {
        ToastService.error(error instanceof Error ? error.message : 'Action DTS impossible');
      }
    })();
  });
};

function renderDtsCreateForm(periodes: string[], selected: string): string {
  return `<form id="dts-form">
    ${FormHelper.select('trimestre', 'Trimestre', optionsTrimestres(periodes), {
      required: true,
      value: selected,
    })}
    <div class="form-field" data-field="notes" style="margin-bottom:12px;">
      <label style="display:block;font-size:0.8rem;color:#586069;margin-bottom:4px;">Notes</label>
      <textarea name="notes" maxlength="500" rows="3"
        style="width:100%;padding:8px 10px;border:1px solid #e1e4e8;border-radius:4px;font:inherit;box-sizing:border-box;"></textarea>
    </div>
    <div style="display:flex;gap:8px;justify-content:flex-end;">
      <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
      <button type="submit" class="btn btn-primary">Générer</button>
    </div>
  </form>`;
}

function renderDtsEditForm(dts: DeclarationDts): string {
  const notes = attrEscape(dts.notes ?? '');
  return `<form id="dts-edit-form">
    ${FormHelper.field('reference_depot', 'Référence de dépôt', 'text', {
      value: dts.reference_depot ?? '',
      placeholder: 'N° de dépôt CNSS',
    })}
    <div class="form-field" data-field="notes" style="margin-bottom:12px;">
      <label style="display:block;font-size:0.8rem;color:#586069;margin-bottom:4px;">Notes</label>
      <textarea name="notes" maxlength="500" rows="3"
        style="width:100%;padding:8px 10px;border:1px solid #e1e4e8;border-radius:4px;font:inherit;box-sizing:border-box;">${notes}</textarea>
    </div>
    <div style="display:flex;gap:8px;justify-content:flex-end;">
      <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
      <button type="submit" class="btn btn-primary">Enregistrer</button>
    </div>
  </form>`;
}

function renderDtsTable(
  section: HTMLElement,
  declarations: DeclarationDts[],
  canMutate: boolean,
): void {
  const tbody = section.querySelector('#dts-table tbody');
  if (!tbody) return;
  const ordered = declarations.slice().sort((a, b) => b.trimestre.localeCompare(a.trimestre));
  if (ordered.length === 0) {
    tbody.innerHTML =
      '<tr><td colspan="7" style="color:var(--gris);padding:14px;">Aucune DTS enregistrée</td></tr>';
    return;
  }
  tbody.innerHTML = ordered
    .map((d) => {
      const actions = renderRowActions(d.id, [
        { action: 'view', label: 'Voir' },
        { action: 'excel-cnss', label: 'Excel CNSS' },
        { action: 'excel-cnamgs', label: 'Excel CNAMGS' },
        { action: 'csv', label: 'CSV' },
        { action: 'csv-mensuel', label: 'CSV mensuel' },
        ...(canMutate && d.statut !== 'annulee'
          ? [{ action: 'edit', label: 'Modifier', adminOnly: true }]
          : []),
        ...(canMutate && d.statut === 'brouillon'
          ? [
              { action: 'regen', label: 'Régénérer', adminOnly: true },
              { action: 'deposit', label: 'Déposer', adminOnly: true },
            ]
          : []),
        ...(canMutate && d.statut !== 'annulee'
          ? [
              {
                action: 'delete',
                label: d.statut === 'brouillon' ? 'Supprimer' : 'Annuler',
                variant: 'danger' as const,
                adminOnly: true,
              },
            ]
          : []),
      ]);
      return `<tr>
        <td><strong>${esc(libelleTrimestre(d.trimestre))}</strong></td>
        <td>${esc(STATUT_DTS[d.statut])}</td>
        <td class="num">${d.effectif}</td>
        <td class="num">${esc(fmtFCFA(d.total_brut))}</td>
        <td class="num">${esc(fmtFCFA(d.total_patronale))}</td>
        <td class="num">${esc(fmtFCFA(d.total_salariale))}</td>
        <td class="num">${actions}</td>
      </tr>`;
    })
    .join('');
}

function sum(list: Bulletin[], f: (b: Bulletin) => number): number {
  return list.reduce((s, b) => s + (f(b) || 0), 0);
}

function renderKpis(section: HTMLElement, list: Bulletin[]): void {
  const el = section.querySelector<HTMLElement>('#declarations-kpis');
  if (!el) return;
  // CNSS : total, part patronale (prestations familiales 5 % + AT-MP 2 % + VID 11 %) et part salariale (VID 5 %) séparés.
  const patronale = sum(list, (b) => ventilationCnssDuBulletin(b).total);
  const salariale = sum(list, (b) => b.cnss_vid_sal);
  const cnss = patronale + salariale;
  const pf = sum(list, (b) => ventilationCnssDuBulletin(b).pf);
  const atmp = sum(list, (b) => ventilationCnssDuBulletin(b).atmp);
  const vid = sum(list, (b) => ventilationCnssDuBulletin(b).vid);
  const cnamgs = sum(list, (b) => b.cnamgs_emp + b.cnamgs_fam_emp + b.cnamgs_sal);
  const irpp = sum(list, (b) => b.irpp_mensuel);
  const taxes = sum(list, (b) => b.tcs + b.cfp_emp + b.fnh_emp);
  el.innerHTML = `
    <div class="kpi"><div class="label">Total CNSS (patronale + salariale)</div><div class="value">${esc(fmtFCFA(cnss))}</div></div>
    <div class="kpi" title="Prestations familiales ${esc(fmtFCFA(pf))} · AT-MP ${esc(fmtFCFA(atmp))} · VID ${esc(fmtFCFA(vid))}"><div class="label">dont CNSS patronale (employeur)</div><div class="value">${esc(fmtFCFA(patronale))}</div><div class="hint" style="font-size:11px;color:var(--gris);">PF ${esc(fmtFCFA(pf))} · AT-MP ${esc(fmtFCFA(atmp))} · VID ${esc(fmtFCFA(vid))}</div></div>
    <div class="kpi"><div class="label">dont CNSS salariale (VID)</div><div class="value">${esc(fmtFCFA(salariale))}</div></div>
    <div class="kpi"><div class="label">Total CNAMGS</div><div class="value">${esc(fmtFCFA(cnamgs))}</div></div>
    <div class="kpi"><div class="label">IRPP retenu</div><div class="value">${esc(fmtFCFA(irpp))}</div></div>
    <div class="kpi"><div class="label">Taxes (TCS+CFP+FNH)</div><div class="value">${esc(fmtFCFA(taxes))}</div></div>`;
}

function renderReversementIRPP(
  section: HTMLElement,
  etat: EtatReversementIRPP,
  canMutate: boolean,
): void {
  const el = section.querySelector<HTMLElement>('#irpp-reversement-body');
  if (!el) return;
  const actions = canMutate
    ? etat.statut === 'reverse'
      ? `<button type="button" class="btn btn-secondary btn-sm" data-on-click="annulerMarquageIRPP()">Annuler le marquage</button>`
      : `<button type="button" class="btn btn-primary btn-sm" data-on-click="marquerReversementIRPP()">Marquer comme reversé</button>`
    : '';
  el.innerHTML = `
    <div style="display:flex;flex-wrap:wrap;gap:16px;align-items:flex-end;justify-content:space-between;">
      <div style="display:flex;flex-wrap:wrap;gap:20px;">
        <div><div class="label" style="font-size:0.75rem;color:var(--gris);">Montant IRPP</div><div class="value" style="font-weight:600;">${esc(fmtFCFA(etat.montant_irpp))}</div></div>
        <div><div class="label" style="font-size:0.75rem;color:var(--gris);">Date limite</div><div class="value" style="font-weight:600;">${esc(etat.date_limite)}</div></div>
        <div><div class="label" style="font-size:0.75rem;color:var(--gris);">Statut</div><div class="value" style="font-weight:600;">${esc(LIB_STATUT_IRPP[etat.statut])}</div></div>
        <div><div class="label" style="font-size:0.75rem;color:var(--gris);">Effectif</div><div class="value" style="font-weight:600;">${esc(String(etat.effectif))}</div></div>
        ${
          etat.date_reversement
            ? `<div><div class="label" style="font-size:0.75rem;color:var(--gris);">Reversé le</div><div class="value" style="font-weight:600;">${esc(etat.date_reversement)}</div></div>`
            : ''
        }
      </div>
      <div style="display:flex;gap:8px;">${actions}</div>
    </div>
    <p style="margin:8px 0 0;font-size:0.8rem;color:var(--gris);">CGI Art. 100 — précompte IRPP (DAMS) à reverser à la DGI avant le 15 du mois suivant.</p>`;
}

type FichesParId = Map<string, Salarie>;

/** Colonnes d'identification communes aux trois bordereaux, miroir du fichier exporté. */
function cellulesIdentite(b: Bulletin, type: TypeDeclaration, fiches: FichesParId): string {
  const salarie = fiches.get(b.salarie_id);
  const nom = b.nom_complet || (salarie ? `${salarie.prenom} ${salarie.nom}` : '') || b.matricule;
  const identifiant = DeclarationsExporter.identifiant(type, salarie);
  return `<td class="mono">${esc(b.matricule || salarie?.matricule || '—')}</td>
        <td>${esc(nom || '—')}</td>
        <td class="mono">${esc(identifiant || '—')}</td>`;
}

/** Rend un bordereau : identité du salarié puis montants déjà formatés. */
function renderBordereau(
  section: HTMLElement,
  selector: string,
  type: TypeDeclaration,
  list: Bulletin[],
  fiches: FichesParId,
  montants: (b: Bulletin) => number[],
): void {
  const tbody = section.querySelector(selector);
  if (!tbody) return;
  const nbColonnes = DeclarationsExporter.COLONNES[type].length;
  tbody.innerHTML =
    list.length === 0
      ? `<tr><td colspan="${nbColonnes}" style="color:var(--gris);padding:14px;">Aucun bulletin</td></tr>`
      : list
          .map(
            (b) => `<tr>
        ${cellulesIdentite(b, type, fiches)}
        ${montants(b)
          .map((v) => `<td class="num">${esc(fmtFCFA(v))}</td>`)
          .join('\n        ')}
      </tr>`,
          )
          .join('');
}

function renderCnss(section: HTMLElement, list: Bulletin[], fiches: FichesParId): void {
  renderBordereau(section, '#cnss-table tbody', 'cnss', list, fiches, (b) =>
    DeclarationsExporter.montantsCnss(b),
  );
}

function renderCnamgs(section: HTMLElement, list: Bulletin[], fiches: FichesParId): void {
  renderBordereau(section, '#cnamgs-table tbody', 'cnamgs', list, fiches, (b) => [
    b.base_cnamgs,
    b.cnamgs_emp + b.cnamgs_fam_emp,
    b.cnamgs_sal,
  ]);
}

function renderDgi(section: HTMLElement, list: Bulletin[], fiches: FichesParId): void {
  renderBordereau(section, '#dgi-table tbody', 'dgi', list, fiches, (b) => [
    b.irpp_mensuel,
    b.tcs,
    b.cfp_emp,
    b.fnh_emp,
  ]);
}
